export type ThemePreference = "dark" | "light" | "system";
export const THEME_STORAGE_KEY = "buddify-color-theme";
export const THEME_CHANGE_EVENT = "buddify-theme-change";

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "dark" || value === "light" || value === "system";
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean) {
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}

export function getThemePreference(): ThemePreference {
  const value = document.documentElement.dataset.theme;
  return isThemePreference(value) ? value : "dark";
}

function applyTheme(preference: ThemePreference) {
  document.documentElement.dataset.theme = preference;
  document.documentElement.dataset.colorScheme = resolveTheme(
    preference,
    window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
}

export function setThemePreference(preference: ThemePreference) {
  applyTheme(preference);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // The current tab still works when the browser blocks persistent storage.
  }
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

export function subscribeTheme(callback: () => void) {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const onSystemChange = () => {
    applyTheme(getThemePreference());
    callback();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY && event.key !== null) return;
    applyTheme(isThemePreference(event.newValue) ? event.newValue : "dark");
    callback();
  };
  media.addEventListener("change", onSystemChange);
  window.addEventListener("storage", onStorage);
  window.addEventListener(THEME_CHANGE_EVENT, callback);
  return () => {
    media.removeEventListener("change", onSystemChange);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(THEME_CHANGE_EVENT, callback);
  };
}

// Runs before body paint; only a validated enum reaches the DOM.
export const themeInitScript = `(() => {
  let preference = "dark";
  try {
    const saved = localStorage.getItem("${THEME_STORAGE_KEY}");
    if (saved === "dark" || saved === "light" || saved === "system") preference = saved;
  } catch {}
  const root = document.documentElement;
  root.dataset.theme = preference;
  root.dataset.colorScheme = preference === "system"
    ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : preference;
})();`;
